import React, { useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Typography,
  TypographyProps
} from '@material-ui/core';
import {
  ExpiryDateVisibility,
  I9DocExpiryDateOptionEnum,
  I9DocumentInfoDto,
  IDocumentUploadState
} from '@swipejobs/i9-document-validations-react';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { observer } from 'mobx-react';
import { cacheTranslations } from '@utils/cache-translations';
import { createTranslationService } from '@services/factories';

enum Resources {
  expiryDateLabel = 'expiryDateLabel',
  NA = 'N/A',
}

enum ErrorResources {
  expiryDateNeedsOptionalValue = 'expiryDateNeedsOptionalValue',
  expiryDateNeedsRequiredValue = 'expiryDateNeedsRequiredValue',
  expiryDateNeedsNotApplicableValue = 'expiryDateNeedsNotApplicableValue',
  noExpirationDateLabel = 'noExpirationDateLabel',
}

interface DocumentExpiryDateFieldProps {
  documentInfo: I9DocumentInfoDto;
  expiryDate: IDocumentUploadState['documentExpiry'];
  checkedNoExpiry: IDocumentUploadState['documentExpiryNotApplicable'];
  onDateChange: (date: IDocumentUploadState['documentExpiry']) => void;
  onNoExpiryCheckChange: (checked: IDocumentUploadState['documentExpiryNotApplicable']) => void;
  error: boolean;
  labelProps?: Exclude<TypographyProps, 'children'>;
}

const DocumentExpiryDateField = observer((props: DocumentExpiryDateFieldProps) => {
  const {
    documentInfo,
    error,
    expiryDate,
    checkedNoExpiry,
    onDateChange,
    onNoExpiryCheckChange,
    labelProps,
  } = props;

  const resources = cacheTranslations(
    createTranslationService(),
    Resources,
    'i9DocumentUpload.uploadSingleDocumentPort'
  );

  const errorResources = cacheTranslations(
    createTranslationService(),
    ErrorResources,
    'i9DocumentUpload'
  );

  const [isValidDate, setIsValidDate] = useState(false);

  useEffect(() => {
    if (documentInfo.expiryDateOption === I9DocExpiryDateOptionEnum.NOT_APPLICABLE) {
      setIsValidDate(true);
    }
  }, [documentInfo]);

  if (documentInfo.expiryDateOption === I9DocExpiryDateOptionEnum.NO_EXPIRY_DATE) return null;

  const showNACheckbox = ExpiryDateVisibility.NA_Checkbox.includes(documentInfo.expiryDateOption);
  const showDatePicker = ExpiryDateVisibility.dateField.includes(documentInfo.expiryDateOption);

  const isExpiryFieldError = () => {
    if (!error) return false;
    if (showNACheckbox && showDatePicker) return !isValidDate || !checkedNoExpiry;
    if (showDatePicker) return !isValidDate;
    return !checkedNoExpiry;
  };

  const getErrorText = () => {
    if (!isExpiryFieldError()) return '';
    if (showNACheckbox && showDatePicker) return errorResources.expiryDateNeedsOptionalValue;
    if (showDatePicker) return errorResources.expiryDateNeedsRequiredValue;
    return errorResources.expiryDateNeedsNotApplicableValue;
  };

  return (
    <Box>
      <Typography {...labelProps}>{resources.expiryDateLabel}</Typography>
      <div>
        <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
          {ExpiryDateVisibility.dateField.includes(documentInfo.expiryDateOption) && (
            <FormControl>
              <KeyboardDatePicker
                disableToolbar={true}
                autoOk={true}
                variant='inline'
                format='MM/DD/YYYY'
                margin='normal'
                inputVariant='outlined'
                style={{ margin: 0 }}
                error={error}
                value={expiryDate}
                disablePast={!documentInfo.expiredDocumentAllowed}
                onChange={(e) => {
                  setIsValidDate(e?.isValid() ?? true);
                  onDateChange(e ? e.toDate() : null);
                  onNoExpiryCheckChange(false);
                }}
              />
            </FormControl>
          )}
          {ExpiryDateVisibility.NA_Checkbox.includes(documentInfo.expiryDateOption) && (
            <FormControlLabel
              label={errorResources.noExpirationDateLabel}
              control={
                <Checkbox
                  style={isExpiryFieldError() ? { color: '#f44336' } : {}}
                  checked={checkedNoExpiry ?? false}
                  onChange={({ target: { checked } }) => {
                    setIsValidDate(false);
                    onNoExpiryCheckChange(checked);
                    onDateChange(null);
                  }}
                />
              }
            />
          )}
        </div>
        {isExpiryFieldError() && <FormHelperText error={true}>{getErrorText()}</FormHelperText>}
      </div>
    </Box>
  );
});

export default DocumentExpiryDateField;



// WEBPACK FOOTER //
// ./src/screens/review-section2/document-upload/DocumentExpiryDateField.tsx